import classNames from "classnames";
import { AsYouType, CountryCode, getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { FocusEventHandler, ReactNode, useState } from "react";
import { parsePhoneNumber } from "../../helperFunctions";
import { COUNTRIES, PRIORITY_COUNTRY_CODES } from "../../utils/constants";
import { Icon } from "../icons/icon";
import { DropdownNew } from "./dropdownNew";
import { TextInput } from "./textInput";

export const PhoneInputSet = ({
  className,
  value,
  onChange,
  onBlur,
  errors,
  name,
  disabled,
  placeholder = "Enter Phone Number",
  title = "Phone Number",
  infoElement,
  inputClassName = "",
  dropdownClassName = "",
}: {
  className?: string;
  value: string;
  onChange: (val: string) => void;
  onBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  errors?: string;
  name: string;
  disabled?: boolean;
  placeholder?: string;
  title?: string;
  infoElement?: ReactNode;
  inputClassName?: string;
  dropdownClassName?: string;
}) => {
  const [search, setSearch] = useState<string>("");
  const [country, setCountry] = useState<CountryCode>(
    value ? parsePhoneNumber(value)?.country ?? "NG" : "NG"
  );

  return (
    <div className={className}>
      <p className={`text-sm text-pry-dark-green-300 font-medium mb-1`}>
        {title}
      </p>

      <div className={classNames("flex items-start gap-[6px]")}>
        <DropdownNew
          position="bottom start"
          customMenuItems={({ close }) => {
            return (
              <div className="relative flex max-h-[312px] max-w-[234px] flex-col overflow-y-scroll rounded-lg border border-grey-100 bg-white text-sm">
                <div className="sticky top-0 flex items-center gap-2 border-b bg-white p-4 text-grey-500">
                  <Icon svg="search" width={16} height={16} />
                  <input
                    type="text"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value ?? "");
                    }}
                    className="flex-1 outline-none autofill:bg-transparent focus:outline-none autofill:focus:bg-transparent"
                  />
                </div>
                {Object.keys(COUNTRIES)
                  .filter((k) =>
                    search
                      ? COUNTRIES[k].name
                          .toLowerCase()
                          .includes(search?.toLowerCase())
                      : true
                  )
                  .sort((a, b) => {
                    const aIsPriority = PRIORITY_COUNTRY_CODES.includes(
                      a as CountryCode
                    );
                    const bIsPriority = PRIORITY_COUNTRY_CODES.includes(
                      b as CountryCode
                    );

                    if (aIsPriority && bIsPriority) {
                      return (
                        PRIORITY_COUNTRY_CODES.indexOf(a as CountryCode) -
                        PRIORITY_COUNTRY_CODES.indexOf(b as CountryCode)
                      );
                    }

                    if (aIsPriority) return -1;
                    if (bIsPriority) return 1;

                    return COUNTRIES[a].name.localeCompare(COUNTRIES[b].name);
                  })
                  .map((k) => {
                    return (
                      <button
                        key={k}
                        type="button"
                        className="w-full border-b p-4 text-left font-bold last:border-b-0"
                        onClick={() => {
                          setSearch("");
                          onChange(COUNTRIES[k].countryCode);
                          setCountry(k as CountryCode);
                          close();
                        }}
                      >
                        {COUNTRIES[k].flag} {COUNTRIES[k].name} (
                        {COUNTRIES[k].countryCode})
                      </button>
                    );
                  })}
              </div>
            );
          }}
          className="h-full !max-w-fit items-center justify-center"
          closeOnClick={false}
          disabled={disabled}
        >
          <button
            type="button"
            className={`w-fit flex-1 gap-2 items-center flex rounded-lg bg-light-green-bg px-3 py-2.5 text-lg font-bold ${dropdownClassName}`}
            disabled={disabled}
          >
            <span>{COUNTRIES[country].flag}</span>
            <Icon
              svg="caret-down"
              width={12}
              height={12}
              className="text-neut-700"
            />
          </button>
        </DropdownNew>

        <TextInput
          title={title}
          hasTitle={false}
          name={name}
          type="text"
          placeholder={placeholder}
          value={value}
          readOnly={disabled}
          disabled={disabled}
          onChange={(e) => {
            if (disabled) {
              return;
            }

            const countryDial = COUNTRIES[country].countryCode; // e.g. "+234"

            const example = getExampleNumber(country, examples);

            const nationalMaxLength =
              (example?.nationalNumber.length ?? 0) + countryDial.length;

            const rawDigits = e.target.value
              .replace(/\D/g, "")
              .slice(0, nationalMaxLength); // user typed digits only

            let fullNumber = "";

            if (
              rawDigits === countryDial.replace("+", "") ||
              rawDigits.length < countryDial.replace("+", "").length
            ) {
              fullNumber = countryDial;
            } else if (
              rawDigits.startsWith(countryDial.replace("+", "")) &&
              rawDigits !== countryDial.replace("+", "")
            ) {
              fullNumber =
                countryDial + rawDigits.slice(countryDial.length - 1);
            } else {
              fullNumber = countryDial + rawDigits;
            }

            const formatter = new AsYouType();
            const formatted = formatter.input(fullNumber);

            onChange(formatted);
          }}
          onBlur={onBlur}
          errors={errors}
          containerClassName="flex-1"
          className={inputClassName}
          hasIcon={false}
        />
      </div>

      {infoElement && (
        <div className="mt-2 text-xs text-primary-80 !leading-5">
          {infoElement}
        </div>
      )}
    </div>
  );
};
